import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PlayerProvider } from './context/PlayerContext';
import { ChangelogProvider } from './context/ChangelogContext';
import { useChangelog } from './hooks/useChangelog';

// Pages
import Home               from './pages/Home';
import AuthPage           from './pages/AuthPage';
import ProfileRouter      from './pages/ProfileRouter';
import Dashboard          from './pages/Dashboard';
import Discover           from './pages/Discover';
import FanProfile         from './pages/profiles/FanProfile';
import DropDetail         from './pages/DropDetail';
import Vault              from './pages/Vault';
import ReleaseCalendar    from './pages/ReleaseCalendar';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import PlatformAdminDashboard from './pages/admin/PlatformAdminDashboard';
import LabelAdminDashboard    from './pages/admin/LabelAdminDashboard';
import SetupAdmin         from './pages/admin/SetupAdmin';
import HelpCenter         from './pages/HelpCenter';
import ForArtists         from './pages/ForArtists';
import PrivacyPolicy      from './pages/PrivacyPolicy';
import TermsOfService     from './pages/TermsOfService';
import NewsletterPreferences from './pages/NewsletterPreferences';
import Changelog          from './pages/Changelog';

// Components
import LoadingScreen  from './components/common/LoadingScreen';
import Player         from './components/Player';
import OnboardingGate from './components/onboarding/OnboardingGate';

// Changelog UI components
import WhatsNewModal from './components/changelog/WhatsNewModal';
import DeployToast   from './components/changelog/DeployToast';
import DeployBanner  from './components/changelog/DeployBanner';

const RoleRoute = ({ element, roles, fallback = '/' }) => {
  const { user, profile, loading, profileLoading } = useAuth();
  if (loading || profileLoading) return <LoadingScreen message="Checking access…" />;
  if (!user) return <Navigate to="/auth" replace />;
  if (roles && !roles.includes(profile?.role)) return <Navigate to={fallback} replace />;
  return element;
};

const AppRoutes = () => {
  const { loading } = useAuth();
  const navigate = useNavigate();

  const {
    showModal, showToast, showBanner,
    entry, toastMessage, liveDeployment,
    dismiss, dismissToast, dismissBanner,
    expandToModal,
  } = useChangelog();

  if (loading) return <LoadingScreen message="Loading…" />;

  const handleReadMore = () => {
    dismiss();
    navigate('/changelog');
  };

  return (
    <>
      <DeployBanner
        show={showBanner}
        headline={liveDeployment?.headline || entry?.headline}
        onRefresh={() => dismissBanner(true)}
        onDismiss={() => dismissBanner(false)}
      />

      <OnboardingGate />

      <Routes>
        <Route path="/"            element={<Home />} />
        <Route path="/auth"        element={<AuthPage />} />
        <Route path="/discover"    element={<Discover />} />
        <Route path="/drop/:id"    element={<DropDetail />} />
        <Route path="/setup-admin" element={<SetupAdmin />} />
        <Route path="/help"        element={<HelpCenter />} />
        <Route path="/artists"     element={<ForArtists />} />
        <Route path="/privacy"     element={<PrivacyPolicy />} />
        <Route path="/terms"       element={<TermsOfService />} />
        <Route path="/newsletter"  element={<NewsletterPreferences />} />
        <Route path="/changelog"   element={<Changelog />} />

        <Route path="/profile" element={<RoleRoute element={<FanProfile />} roles={null} />} />
        <Route path="/vault"   element={<RoleRoute element={<Vault />}      roles={null} />} />

        <Route path="/dashboard" element={
          <RoleRoute element={<Dashboard />} roles={['artist', 'label_admin', 'platform_admin']} />
        } />
        <Route path="/analytics" element={
          <RoleRoute element={<AnalyticsDashboard />} roles={['artist', 'label_admin', 'platform_admin']} />
        } />
        <Route path="/calendar" element={
          <RoleRoute element={<ReleaseCalendar />} roles={['artist', 'label_admin', 'platform_admin']} />
        } />

        <Route path="/admin" element={
          <RoleRoute element={<PlatformAdminDashboard />} roles={['platform_admin']} fallback="/" />
        } />
        <Route path="/label-admin" element={
          <RoleRoute element={<LabelAdminDashboard />} roles={['label_admin', 'platform_admin']} fallback="/" />
        } />

        <Route path="/:slug" element={<ProfileRouter />} />
      </Routes>

      <Player />

      <DeployToast
        show={showToast}
        message={toastMessage}
        entry={entry}
        liveDeployment={liveDeployment}
        onDismiss={dismissToast}
        onExpand={expandToModal}
      />

      {showModal && (
        <WhatsNewModal
          entry={entry}
          liveDeployment={liveDeployment}
          onDismiss={dismiss}
          onReadMore={handleReadMore}
        />
      )}
    </>
  );
};

export default function App() {
  return (
    <AuthProvider>
      <PlayerProvider>
        <ChangelogProvider>
          <Router>
            <AppRoutes />
          </Router>
        </ChangelogProvider>
      </PlayerProvider>
    </AuthProvider>
  );
}